<template>
  <div class="issue-tab">
    <!-- 요약 배지 -->
    <div class="tab-header">
      <div class="issue-summary">
        <el-badge :value="counts.OPEN" type="danger" class="badge-item">
          <el-tag type="danger">미해결</el-tag>
        </el-badge>
        <el-badge :value="counts.IN_PROGRESS" type="warning" class="badge-item">
          <el-tag type="warning">처리중</el-tag>
        </el-badge>
        <el-badge :value="counts.RESOLVED" type="success" class="badge-item">
          <el-tag type="success">해결</el-tag>
        </el-badge>
        <el-badge :value="counts.CLOSED" type="info" class="badge-item">
          <el-tag type="info">종료</el-tag>
        </el-badge>
      </div>
      <el-button v-if="canEdit" type="primary" size="small" @click="openForm()">
        이슈 등록
      </el-button>
    </div>

    <!-- 필터 행 -->
    <div class="filter-row">
      <div style="display: flex; gap: 12px">
        <el-select v-model="filterStatus" placeholder="상태" clearable style="width: 120px">
          <el-option label="미해결" value="OPEN" />
          <el-option label="처리중" value="IN_PROGRESS" />
          <el-option label="해결" value="RESOLVED" />
          <el-option label="종료" value="CLOSED" />
        </el-select>
        <el-select v-model="filterPriority" placeholder="우선순위" clearable style="width: 120px">
          <el-option label="낮음" value="LOW" />
          <el-option label="보통" value="MEDIUM" />
          <el-option label="높음" value="HIGH" />
          <el-option label="긴급" value="URGENT" />
        </el-select>
      </div>
    </div>

    <!-- 이슈 테이블 -->
    <el-table :data="filteredIssues" border size="small" stripe v-loading="loading">
      <el-table-column label="우선순위" width="90" align="center">
        <template #default="{ row }">
          <el-tag :type="priorityType(row.priority)" size="small">
            {{ priorityDisplay(row.priority) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="title" label="제목" min-width="200" />
      <el-table-column label="상태" width="90" align="center">
        <template #default="{ row }">
          <el-tag :type="statusType(row.status)" size="small">
            {{ statusDisplay(row.status) }}
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column prop="assignee_name" label="담당자" width="100" />
      <el-table-column prop="created_at" label="등록일" width="110">
        <template #default="{ row }">
          {{ formatDate(row.created_at) }}
        </template>
      </el-table-column>
      <el-table-column prop="resolved_at" label="해결일" width="110">
        <template #default="{ row }">
          {{ row.resolved_at ? formatDate(row.resolved_at) : '-' }}
        </template>
      </el-table-column>
      <el-table-column label="작업" width="120" align="center" v-if="canEdit">
        <template #default="{ row }">
          <el-button type="primary" link size="small" @click="openForm(row)">
            수정
          </el-button>
          <el-divider direction="vertical" />
          <el-button
            v-if="row.status !== 'CLOSED'"
            type="success"
            link
            size="small"
            @click="handleResolve(row)"
          >
            해결
          </el-button>
          <span v-else style="color: #c0c4cc">-</span>
        </template>
      </el-table-column>
    </el-table>

    <el-empty v-if="issues.length === 0 && !loading" description="등록된 이슈가 없습니다." />

    <!-- 등록/수정 다이얼로그 -->
    <el-dialog
      v-model="formVisible"
      :title="editingId ? '이슈 수정' : '이슈 등록'"
      width="700px"
    >
      <el-form :model="form" label-width="100px" :rules="formRules" ref="formRef">
        <el-form-item label="제목" prop="title">
          <el-input v-model="form.title" placeholder="이슈 제목을 입력하세요" />
        </el-form-item>

        <el-form-item label="설명" prop="description">
          <el-input
            v-model="form.description"
            type="textarea"
            :rows="5"
            placeholder="상세 설명을 입력하세요"
          />
        </el-form-item>

        <el-row :gutter="16">
          <el-col :span="12">
            <el-form-item label="우선순위" prop="priority">
              <el-select v-model="form.priority" style="width: 100%">
                <el-option label="낮음" value="LOW" />
                <el-option label="보통" value="MEDIUM" />
                <el-option label="높음" value="HIGH" />
                <el-option label="긴급" value="URGENT" />
              </el-select>
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item label="상태" prop="status">
              <el-select v-model="form.status" style="width: 100%">
                <el-option label="미해결" value="OPEN" />
                <el-option label="처리중" value="IN_PROGRESS" />
                <el-option label="해결" value="RESOLVED" />
                <el-option label="종료" value="CLOSED" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-alert
          title="담당자 지정"
          type="info"
          :closable="false"
          description="담당자 지정 기능은 추후 구현될 예정입니다. 현재는 생성자가 담당자로 지정됩니다."
          style="margin-bottom: 12px"
        />
      </el-form>

      <template #footer>
        <el-button @click="formVisible = false">취소</el-button>
        <el-button
          v-if="editingId && canEdit"
          type="danger"
          @click="handleDelete"
        >
          삭제
        </el-button>
        <el-button type="primary" @click="handleSave" :loading="saving">
          저장
        </el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from 'vue'
import axiosInstance from '@/api/axios'
import { ElMessage, ElMessageBox } from 'element-plus'

const props = defineProps({
  mainRequestId: { type: [Number, String], required: true },
  canEdit: { type: Boolean, default: false },
})

const loading = ref(false)
const saving = ref(false)
const issues = ref([])
const formVisible = ref(false)
const formRef = ref(null)
const editingId = ref(null)

const filterStatus = ref(null)
const filterPriority = ref(null)

const defaultForm = () => ({
  title: '',
  description: '',
  priority: 'MEDIUM',
  status: 'OPEN',
})

const form = ref(defaultForm())

const formRules = {
  title: [{ required: true, message: '제목을 입력하세요', trigger: 'blur' }],
  description: [{ required: true, message: '설명을 입력하세요', trigger: 'blur' }],
  priority: [{ required: true, message: '우선순위를 선택하세요', trigger: 'change' }],
  status: [{ required: true, message: '상태를 선택하세요', trigger: 'change' }],
}

const priorityDisplayMap = {
  LOW: '낮음',
  MEDIUM: '보통',
  HIGH: '높음',
  URGENT: '긴급',
}

const statusDisplayMap = {
  OPEN: '미해결',
  IN_PROGRESS: '처리중',
  RESOLVED: '해결',
  CLOSED: '종료',
}

const priorityDisplay = (priority) => priorityDisplayMap[priority] || priority
const statusDisplay = (status) => statusDisplayMap[status] || status

const priorityType = (priority) => {
  const map = {
    LOW: 'info',
    MEDIUM: 'warning',
    HIGH: 'danger',
    URGENT: 'danger',
  }
  return map[priority] || 'info'
}

const statusType = (status) => {
  const map = {
    OPEN: 'danger',
    IN_PROGRESS: 'warning',
    RESOLVED: 'success',
    CLOSED: 'info',
  }
  return map[status] || 'info'
}

const counts = computed(() => {
  const c = {
    OPEN: 0,
    IN_PROGRESS: 0,
    RESOLVED: 0,
    CLOSED: 0,
  }
  issues.value.forEach((issue) => {
    if (Object.prototype.hasOwnProperty.call(c, issue.status)) {
      c[issue.status]++
    }
  })
  return c
})

const filteredIssues = computed(() => {
  return issues.value.filter((issue) => {
    const statusMatch = !filterStatus.value || issue.status === filterStatus.value
    const priorityMatch = !filterPriority.value || issue.priority === filterPriority.value
    return statusMatch && priorityMatch
  })
})

const formatDate = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleDateString('ko-KR')
}

const fetchIssues = async () => {
  if (!props.mainRequestId) return
  loading.value = true
  try {
    const res = await axiosInstance.get(`/production/issues/`, {
      params: { main_request: props.mainRequestId }
    })
    issues.value = res.data.results || res.data || []
  } catch (e) {
    console.error('이슈 조회 실패:', e)
    ElMessage.error('이슈 조회에 실패했습니다.')
  } finally {
    loading.value = false
  }
}

const openForm = (row = null) => {
  if (row) {
    editingId.value = row.id
    form.value = {
      title: row.title,
      description: row.description,
      priority: row.priority,
      status: row.status,
    }
  } else {
    editingId.value = null
    form.value = defaultForm()
  }
  formVisible.value = true
}

const handleSave = async () => {
  if (!formRef.value) return
  try {
    await formRef.value.validate()
  } catch {
    return
  }

  saving.value = true
  try {
    const payload = {
      main_request: props.mainRequestId,
      title: form.value.title,
      description: form.value.description,
      priority: form.value.priority,
      status: form.value.status,
    }

    if (editingId.value) {
      await axiosInstance.put(`/production/issues/${editingId.value}/`, payload)
      ElMessage.success('이슈가 수정되었습니다.')
    } else {
      await axiosInstance.post(`/production/issues/`, payload)
      ElMessage.success('이슈가 등록되었습니다.')
    }
    formVisible.value = false
    await fetchIssues()
  } catch (e) {
    ElMessage.error('저장 실패: ' + (e.response?.data?.detail || e.message || '오류 발생'))
  } finally {
    saving.value = false
  }
}

const handleDelete = async () => {
  try {
    await ElMessageBox.confirm('이 이슈를 삭제하시겠습니까?', '삭제 확인', {
      confirmButtonText: '삭제',
      cancelButtonText: '취소',
      type: 'warning',
    })
    await axiosInstance.delete(`/production/issues/${editingId.value}/`)
    ElMessage.success('삭제되었습니다.')
    formVisible.value = false
    await fetchIssues()
  } catch {
    // 취소
  }
}

const handleResolve = async (issue) => {
  try {
    await ElMessageBox.confirm(
      '이 이슈를 해결 상태로 변경하시겠습니까?',
      '해결 처리',
      {
        confirmButtonText: '확인',
        cancelButtonText: '취소',
        type: 'info',
      }
    )
    await axiosInstance.post(`/production/issues/${issue.id}/resolve/`)
    ElMessage.success('이슈가 해결 상태로 변경되었습니다.')
    await fetchIssues()
  } catch {
    // 취소
  }
}

watch(() => props.mainRequestId, fetchIssues, { immediate: true })

onMounted(() => {
  fetchIssues()
})
</script>

<style scoped>
.issue-tab {
  padding: 8px 0;
}

.tab-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
}

.issue-summary {
  display: flex;
  gap: 16px;
}

.badge-item {
  display: inline-flex;
}

.filter-row {
  margin-bottom: 16px;
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
}

.filter-row > div {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}
</style>
